import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { hasAnyPermission } from "../lib/permissions";

const LandingPage = () => {
  const navigate = useNavigate();

  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Group modules parent-wise
  const moduleSections = [
    {
      parent: "Operations",
      modules: [
        {
          title: "Retail Management",
          path: "/retail_dashboard",
          requiredPermissions: ["main_admin.view", "main_admin.edit"],
          logo: (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-indigo-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
              />
            </svg>
          ),
        },
        {
          title: "QC Process",
          path: "/warehouse_dashboard",
          requiredPermissions: ["quality_check.view", "quality_check.edit"],
          logo: (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-amber-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              />
            </svg>
          ),
        },
      ],
    },

    {
      parent: "Quality Control",
      modules: [
        {
          title: "Images & Remarks",
          path: "/secure/quality-check",
          requiredPermissions: ["qc_admin.view", "qc_admin.edit", "quality_check.view", "quality_check.edit"],
          logo: (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-emerald-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          ),
        },
      ],
    },

    {
      parent: "Purchase",
      modules: [
        {
          title: "Party Details",
          path: "/purchase/party-details",
          requiredPermissions: ["purchase_admin.view", "purchase_admin.edit"],
          logo: (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          ),
        },
        {
          title: "Requirements",
          path: "/purchase/requirements",
          requiredPermissions: ["purchase_admin.view", "purchase_admin.edit"],
          logo: (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
          ),
        },
        {
          title: "Invoicing Details",
          path: "/purchase/invoicing",
          requiredPermissions: ["purchase_admin.view", "purchase_admin.edit", "purchase_vendor.view", "purchase_vendor.edit", "purchase_user.view", "purchase_user.edit"],
          logo: (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          ),
        },
      ],
    },

    {
      parent: "Inventory",
      modules: [
        {
          title: "Inventory Management",
          path: "/inventory-management-dashboard",
          requiredPermissions: ["inventory.view", "inventory.edit"],
          logo: (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-blue-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
          ),
        },
        {
          title: "SKU Management",
          path: "/sku-management",
          requiredPermissions: ["sku_management.view", "sku_management.edit", "sku_management.create", "sku_management.delete"],
          logo: (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-rose-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
              />
            </svg>
          ),
        },
      ],
    },
  ];

  const canAccess = (module) =>
    module.requiredPermissions.length === 0 || hasAnyPermission(module.requiredPermissions);

  const visibleSections = moduleSections
    .map((section) => ({
      ...section,
      modules: section.modules.filter(canAccess),
    }))
    .filter((section) => section.modules.length > 0);

  const handleModuleClick = (module) => {
    if (!canAccess(module)) {
      alert("You do not have access to this module.");
      return;
    }
    setMobileSidebarOpen(false);
    navigate(module.path);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar title="Dashboard" />

      {/* Mobile Menu Button */}
      <div className="md:hidden px-4 pt-4">
        <button
          onClick={() => setMobileSidebarOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg shadow"
        >
          {/* Hamburger */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>

          Modules
        </button>
      </div>

      <div className="flex">
        {/* Desktop Sidebar */}


        {/* Mobile Sidebar Overlay */}
        {mobileSidebarOpen && (
          <>
            {/* Background */}
            <div
              className="fixed inset-0 bg-black bg-opacity-40 z-40 md:hidden"
              onClick={() => setMobileSidebarOpen(false)}
            />

            {/* Sidebar */}
            <div className="fixed top-0 left-0 h-full w-72 bg-white z-50 shadow-xl md:hidden overflow-y-auto">
              <div className="flex items-center justify-between p-4 border-b">
                <h2 className="text-lg font-semibold">Modules</h2>

                <button onClick={() => setMobileSidebarOpen(false)}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-gray-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="p-4">
                <SidebarContent
                  sections={visibleSections}
                  canAccess={canAccess}
                  handleModuleClick={handleModuleClick}
                />
              </div>
            </div>
          </>
        )}

        {/* Main Content */}
        <div className="flex-1 p-4 md:p-8">
          <h1 className="text-2xl font-semibold text-gray-800 mb-8">
            Select Module
          </h1>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
            {visibleSections.map((section, sectionIndex) => (
              <div
                key={sectionIndex}
                className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm"
              >
                <h2 className="text-lg font-semibold text-gray-800 mb-4">
                  {section.parent}
                </h2>

                <div className="space-y-3">
                  {section.modules.map((module, index) => {
                    const hasAccess = canAccess(module);

                    return (
                      <div
                        key={index}
                        onClick={() => handleModuleClick(module)}
                        className={`p-4 rounded-lg border transition-all flex items-center justify-between ${
                          hasAccess
                            ? "cursor-pointer bg-gray-50 hover:bg-indigo-50 hover:border-indigo-300"
                            : "bg-gray-100 border-gray-300 opacity-60 cursor-not-allowed"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-md bg-white shadow-sm">
                            {module.logo}
                          </div>

                          <span className="font-medium text-gray-700">
                            {module.title}
                          </span>
                        </div>

                        {hasAccess && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 text-gray-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const SidebarContent = ({ sections, canAccess, handleModuleClick }) => {
  return (
    <div className="space-y-6">
      {sections.map((section, index) => (
        <div key={index}>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            {section.parent}
          </h3>

          <div className="space-y-2">
            {section.modules.map((module, idx) => {
              const hasAccess = canAccess(module);

              return (
                <div
                  key={idx}
                  onClick={() => handleModuleClick(module)}
                  className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                    hasAccess
                      ? "cursor-pointer hover:bg-indigo-50"
                      : "opacity-50 cursor-not-allowed"
                  }`}
                >
                  <div className="p-2 rounded-md bg-gray-100">
                    {module.logo}
                  </div>

                  <span className="text-sm font-medium text-gray-700">
                    {module.title}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default LandingPage;
